import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, MessageCircle } from "lucide-react";
import { scrollToId, waLink, DEFAULT_WA_MESSAGE } from "@/lib/site";

const LINKS = [
  { id: "home", label: "Home", testId: "nav-link-home" },
  { id: "about", label: "About Us", testId: "nav-link-about" },
  { id: "products", label: "Products", testId: "nav-link-products" },
  { id: "why-us", label: "Why Choose Us", testId: "nav-link-why-us" },
  { id: "contact", label: "Contact Us", testId: "nav-link-contact" },
];

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <header
      data-testid="main-navbar"
      className={`sticky top-0 z-50 border-b transition-colors duration-300 ${
        scrolled
          ? "bg-[#FBF8F5]/90 backdrop-blur-xl border-[#E7DFD5]"
          : "bg-[#FBF8F5]/60 backdrop-blur-md border-transparent"
      }`}
    >
      <div className="section-shell flex h-[72px] items-center justify-between">
        <button
          data-testid="brand-logo-link"
          onClick={() => go("home")}
          className="flex flex-col items-start leading-none"
          aria-label="JK Furniture home"
        >
          <span className="font-serif text-xl sm:text-2xl font-semibold tracking-tight text-walnut">
            JK <span className="italic text-teak">Furniture</span>
          </span>
          <span className="mt-1 font-mono text-[10px] uppercase tracking-[0.28em] text-stone-500">
            Estd. Mumbai
          </span>
        </button>

        <nav className="hidden lg:flex items-center gap-8" aria-label="Primary">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={l.testId}
              onClick={() => go(l.id)}
              className="text-sm font-medium text-stone-600 hover:text-walnut transition-colors duration-200 tracking-wide"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            data-testid="nav-cta-whatsapp-quick"
            href={waLink(DEFAULT_WA_MESSAGE)}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Chat on WhatsApp"
            className="hidden sm:flex h-10 w-10 items-center justify-center rounded-full border border-[#E7DFD5] text-[#1EBE5D] hover:bg-[#25D366] hover:text-white hover:border-[#25D366] transition-colors duration-200"
          >
            <MessageCircle className="h-4.5 w-4.5" size={18} />
          </a>
          <button
            data-testid="nav-cta-shop-now"
            onClick={() => go("showcase")}
            className="hidden sm:inline-flex items-center rounded-full bg-walnut px-6 py-2.5 text-sm font-semibold text-sand transition-transform duration-200 hover:-translate-y-0.5 hover:bg-noir"
          >
            Shop Now
          </button>
          <button
            data-testid="nav-mobile-menu-button"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden h-10 w-10 flex items-center justify-center rounded-full border border-[#E7DFD5] text-walnut"
            aria-label="Toggle menu"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: [0.215, 0.61, 0.355, 1] }}
            className="lg:hidden overflow-hidden border-t border-[#E7DFD5] bg-[#FBF8F5]"
            aria-label="Mobile"
          >
            <div className="section-shell flex flex-col py-4">
              {LINKS.map((l) => (
                <button
                  key={l.id}
                  data-testid={`mobile-${l.testId}`}
                  onClick={() => go(l.id)}
                  className="py-3 text-left font-serif text-lg text-walnut border-b border-[#F0E9E0] last:border-0"
                >
                  {l.label}
                </button>
              ))}
              <button
                data-testid="mobile-nav-cta-shop-now"
                onClick={() => go("showcase")}
                className="mt-4 rounded-full bg-walnut px-6 py-3 text-sm font-semibold text-sand"
              >
                Shop Now
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
